import { PasswordInput } from '../../desktop/src/renderer/ui/password-input';
import { useDialogFocus } from '../../desktop/src/renderer/workbench-shared';
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent, type JSX } from 'react';
import type { Session } from '@supabase/supabase-js';
import { App as WorkshopApp } from '@desktop-renderer/App';
import {
  changePassword,
  cloudConfigured,
  configureLoginPreferences,
  currentAuthNotice,
  currentSession,
  demoMode,
  initialAuthCallbackIssue,
  loadProfile,
  readLoginPreferences,
  requestInitialPasswordLink,
  setInitialPassword,
  signIn,
  signOut,
  supabase,
  type AccountProfile,
} from './cloud';
import {
  authorizeCurrentDevice,
  DeviceReplacementLimitError,
  DeviceReplacementRequiredError,
  hasReusableDeviceSession,
  loadDeviceOverview,
  updateDeviceAccessToken,
  type DeviceOverview,
  type DeviceReplacementState,
} from './device-security';
import { createWebRuntime } from './web-runtime';
import { AuthBootGate, isInvalidSessionError, SESSION_EXPIRED_MESSAGE } from './auth-session';
import { ChevronDownRegular, DismissRegular, SpinnerIosRegular } from '@fluentui/react-icons';
const AdminPage = lazy(() => import('./AdminPage').then(module => ({ default: module.AdminPage })));

type ShellState =
  | { kind: 'loading'; detail: string }
  | { kind: 'signed-out'; message?: string }
  | { kind: 'password-setup'; profile: AccountProfile; session: Session }
  | { kind: 'device-replacement'; profile: AccountProfile; session: Session; replacement: DeviceReplacementState; message?: string }
  | { kind: 'ready'; profile: AccountProfile; session: Session }
  | { kind: 'error'; message: string };

export function AppShell(): JSX.Element {
  const [state, setState] = useState<ShellState>({ kind: 'loading', detail: '验证账户并同步工作数据' });
  const [path, setPath] = useState(location.pathname);
  const [bootVersion, setBootVersion] = useState(0);
  const [accountOpen, setAccountOpen] = useState(false);
  const activeAuthUserId = useRef<string | undefined>(undefined);
  const bootGate = useRef(new AuthBootGate());
  useEffect(() => {
    const openAccount = () => setAccountOpen(true);
    window.addEventListener('minglun:open-account', openAccount);
    return () => window.removeEventListener('minglun:open-account', openAccount);
  }, []);
  useEffect(() => {
    const update = (): void => setPath(location.pathname);
    addEventListener('popstate', update);
    return () => removeEventListener('popstate', update);
  }, []);
  useEffect(() => {
    let active = true;
    const ticket = bootGate.current.begin();
    const isCurrent = () => active && bootGate.current.isCurrent(ticket);
    const boot = async (): Promise<void> => {
      try {
        // Account changes must tear down the previous in-memory workbench
        // before loading the next profile. Otherwise React can preserve the
        // former user's plan and developer state under the new account shell.
        if (isCurrent()) setState({ kind: 'loading', detail: '验证账户并同步工作数据' });
        const session = await currentSession();
        if (!isCurrent()) return;
        if (!demoMode && !session) {
          activeAuthUserId.current = undefined;
          const message = initialAuthCallbackIssue ?? currentAuthNotice();
          setState({ kind: 'signed-out', ...(message ? { message } : {}) });
          return;
        }
        if (session) activeAuthUserId.current = session.user.id;
        const profile = await loadProfile(session);
        if (!isCurrent()) return;
        if (!demoMode && session && profile.mustSetPassword) { setState({ kind: 'password-setup', profile, session }); return; }
        if (!demoMode && session) {
          const reusableDeviceSession = hasReusableDeviceSession(session.user.id);
          if (isCurrent() && !reusableDeviceSession) setState({ kind: 'loading', detail: '正在验证授权设备' });
          try { await authorizeCurrentDevice(session); }
          catch (error) {
            if (error instanceof DeviceReplacementRequiredError || error instanceof DeviceReplacementLimitError) {
              if (isCurrent()) setState({ kind: 'device-replacement', profile, session, replacement: error.state, ...(error instanceof DeviceReplacementLimitError ? { message: error.message } : {}) });
              return;
            }
            throw error;
          }
        }
        if (!isCurrent()) return;
        const runtime = await createWebRuntime(profile, session!);
        if (!isCurrent()) return;
        window.minglun = runtime;
        setState({ kind: 'ready', profile, session: session! });
      } catch (error) {
        if (!isCurrent()) return;
        if (isInvalidSessionError(error)) {
          activeAuthUserId.current = undefined;
          setState({ kind: 'signed-out', message: SESSION_EXPIRED_MESSAGE });
        } else setState({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
      }
    };
    void boot();
    return () => { active = false; };
  }, [bootVersion]);

  useEffect(() => {
    const subscription = supabase?.auth.onAuthStateChange((event, refreshedSession) => {
      updateDeviceAccessToken(refreshedSession);
      if (event === 'SIGNED_OUT') {
        bootGate.current.invalidate();
        activeAuthUserId.current = undefined;
        setAccountOpen(false);
        const message = currentAuthNotice();
        setState({ kind: 'signed-out', ...(message ? { message } : {}) });
      }
      else if ((event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') && refreshedSession && (activeAuthUserId.current !== refreshedSession.user.id || event === 'PASSWORD_RECOVERY')) {
        bootGate.current.invalidate();
        activeAuthUserId.current = refreshedSession.user.id;
        setBootVersion((value) => value + 1);
      }
    }).data.subscription;
    return () => subscription?.unsubscribe();
  }, []);

  if (state.kind === 'loading') return <CenteredState tone="loading" title="正在进入工作台" detail={state.detail} />;
  if (state.kind === 'error') return <CenteredState tone="error" title="无法打开命轮工坊" detail={state.message} actionLabel="重新尝试" onAction={() => setBootVersion((value) => value + 1)} />;
  if (state.kind === 'signed-out') return <LoginPage {...(state.message ? { initialMessage: state.message } : {})} />;
  if (state.kind === 'password-setup') return <PasswordSetupPage email={state.profile.email} onDone={() => setBootVersion((value) => value + 1)} />;
  if (state.kind === 'device-replacement') return <DeviceReplacementPage state={state} onDone={() => setBootVersion((value) => value + 1)} />;
  const navigate = (next: string): void => {
    if (location.pathname !== next) history.pushState({}, '', next);
    setPath(next);
    setAccountOpen(false);
  };
  const adminOpen = path === '/admin';
  const runtimeKey = `${state.profile.id}:${bootVersion}`;
  if (adminOpen && state.profile.role !== 'admin') return <CenteredState tone="error" title="无权访问管理后台" detail="当前账户没有管理员权限。" actionLabel="返回工作台" onAction={() => navigate('/')} />;
  return (
    <>
      <div className="web-runtime" hidden={adminOpen} aria-hidden={adminOpen || undefined}>
        {accountOpen && <AccountPanel profile={state.profile} session={state.session} onClose={() => setAccountOpen(false)} onAdmin={() => navigate('/admin')} />}
        <WorkshopApp key={runtimeKey} runtimeKey={runtimeKey} />
      </div>
      {adminOpen && <Suspense fallback={<CenteredState tone="loading" title="正在打开账户管理" detail="" />}><AdminPage profile={state.profile} onBack={() => navigate('/')} onSignOut={() => void signOut()} /></Suspense>}
    </>
  );
}

function LoginPage({ initialMessage }: { initialMessage?: string }): JSX.Element {
  const initialPreferences = useMemo(() => readLoginPreferences(), []);
  const [email, setEmail] = useState(initialPreferences.email);
  const [password, setPassword] = useState('');
  const [rememberCredentials, setRememberCredentials] = useState(initialPreferences.rememberCredentials);
  const [autoLogin, setAutoLogin] = useState(initialPreferences.autoLogin);
  const [pending, setPending] = useState(false);
  const [registrationPending, setRegistrationPending] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; text: string } | undefined>(initialMessage ? { tone: 'error', text: initialMessage } : undefined);
  const canSubmit = useMemo(() => /.+@.+\..+/.test(email) && password.length >= 6 && !pending, [email, password, pending]);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); if (!canSubmit) return; setPending(true); setNotice(undefined);
    try {
      configureLoginPreferences(email, rememberCredentials, autoLogin);
      await signIn(email.trim(), password);
    } catch (error) { setNotice({ tone: 'error', text: error instanceof Error ? error.message : '登录失败' }); setPending(false); }
  };
  const requestRegistration = async (): Promise<void> => {
    if (!/.+@.+\..+/.test(email) || registrationPending) return;
    setRegistrationPending(true); setNotice(undefined);
    try {
      await requestInitialPasswordLink(email);
      history.replaceState({}, '', location.pathname);
      setNotice({ tone: 'success', text: '如果该邮箱仍待设置密码，新的注册链接已发送，请前往邮箱继续。' });
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : '注册链接发送失败' });
    } finally { setRegistrationPending(false); }
  };
  if (!cloudConfigured && !demoMode) return <CenteredState tone="error" title="站点尚未配置" detail="请由管理员配置 Supabase 地址与匿名密钥后重新发布。" />;
  return (
    <main className="login-screen">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-mark" aria-hidden="true">命</div>
        <div className="login-heading"><span>命轮工坊</span><h1 id="login-title">登录工作台</h1><p>使用受邀邮箱登录。首次使用请打开邀请邮件设置密码。</p></div>
        <form onSubmit={(event) => void submit(event)}>
          <label>登录邮箱<input name="email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" required /></label>
          <label>密码<PasswordInput name="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="输入账户密码" required minLength={6} /></label>
          <div className="login-registration-assist"><span>尚未设置密码？</span><button type="button" disabled={!/.+@.+\..+/.test(email) || registrationPending} onClick={() => void requestRegistration()}>{registrationPending ? '正在发送…' : '发送注册链接'}</button></div>
          <div className="login-preferences" role="group" aria-label="登录偏好">
            <label title="保存邮箱，并允许浏览器密码管理器安全保存密码；应用不会保存明文密码。"><input type="checkbox" checked={rememberCredentials} onChange={(event) => { const checked = event.target.checked; setRememberCredentials(checked); if (!checked) setAutoLogin(false); }} /><span aria-hidden="true" /><b>记住账号密码</b></label>
            <label title="在这台设备上保留登录会话，下次打开时直接进入工作台。"><input type="checkbox" checked={autoLogin} onChange={(event) => { const checked = event.target.checked; setAutoLogin(checked); if (checked) setRememberCredentials(true); }} /><span aria-hidden="true" /><b>自动登录</b></label>
          </div>
          {notice && <div className={`login-message ${notice.tone}`} role={notice.tone === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
          <button className="login-primary" type="submit" disabled={!canSubmit}>{pending ? '正在验证…' : '登录'}</button>
        </form>

      </section>
    </main>
  );
}

function PasswordSetupPage({ email, onDone }: { email: string; onDone: () => void }): JSX.Element {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const valid = password.length >= 8 && password === confirmation;
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); if (!valid || pending) return;
    setPending(true); setError(undefined);
    try { await setInitialPassword(password); onDone(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : '密码设置失败'); setPending(false); }
  };
  return <main className="login-screen"><section className="login-panel setup-panel" aria-labelledby="password-setup-title">
    <div className="setup-step"><span>✓</span><i /><span>邀请邮箱已确认</span></div>
    <div className="login-heading"><span>完成注册</span><h1 id="password-setup-title">设置登录密码</h1><p><strong className="confirmed-email">{email}</strong><br />密码保存后将自动登录，并登记当前设备。</p></div>
    <form onSubmit={(event) => void submit(event)}>
      <label>新密码<PasswordInput autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} placeholder="至少 8 位" autoFocus required /></label>
      <label>确认密码<PasswordInput autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={8} placeholder="再次输入密码" required /></label>
      {confirmation && password !== confirmation && <div className="field-hint error">两次输入的密码不一致。</div>}
      {error && <div className="login-message" role="alert">{error}</div>}
      <button className="login-primary" type="submit" disabled={!valid || pending}>{pending ? '正在完成注册…' : '完成注册并登录'}</button>
    </form>
  </section></main>;
}

function DeviceReplacementPage({ state, onDone }: { state: Extract<ShellState, { kind: 'device-replacement' }>; onDone: () => void }): JSX.Element {
  const [pendingId, setPendingId] = useState<string>();
  const [message, setMessage] = useState(state.message);
  const remaining = Math.max(0, state.replacement.replacementLimit - state.replacement.replacementsUsed);
  const replace = async (deviceId: string): Promise<void> => {
    setPendingId(deviceId); setMessage(undefined);
    try { await authorizeCurrentDevice(state.session, deviceId); onDone(); }
    catch (error) { setMessage(error instanceof Error ? error.message : '设备替换失败'); setPendingId(undefined); }
  };
  return <main className="login-screen"><section className="device-gate" aria-labelledby="device-gate-title">
    <header><span className="device-symbol" aria-hidden="true">◇</span><div><small>设备授权</small><h1 id="device-gate-title">选择要替换的旧设备</h1><p>该账户已使用全部授权名额。替换完成后，旧设备将立即失效。</p></div></header>
    <div className="device-gate-meta"><span>本周可替换 <strong>{remaining}</strong> 次</span><span>已使用 {state.replacement.replacementsUsed}/{state.replacement.replacementLimit}</span></div>
    <div className="device-choice-list">
      {state.replacement.devices.map((device) => <button type="button" key={device.id} disabled={remaining === 0 || Boolean(pendingId)} onClick={() => void replace(device.id)}>
        <span><strong>{device.label}</strong><small>最近使用 {formatDateTime(device.lastSeenAt)}</small></span><b>{pendingId === device.id ? '正在替换…' : '替换'}</b>
      </button>)}
    </div>
    {message && <div className="login-message" role="alert">{message}</div>}
    <footer><button type="button" className="login-link" onClick={() => void signOut()}>使用其他账户</button></footer>
  </section></main>;
}

function AccountPanel({ profile, session, onClose, onAdmin }: { profile: AccountProfile; session: Session; onClose: () => void; onAdmin: () => void }): JSX.Element {
  const dialogRef = useDialogFocus(true, onClose);
  const [overview, setOverview] = useState<DeviceOverview>();
  const [error, setError] = useState<string>();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<string>();
  useEffect(() => { let active = true; loadDeviceOverview(session).then((value) => active && setOverview(value), (reason) => active && setError(reason instanceof Error ? reason.message : String(reason))); return () => { active = false; }; }, [session]);
  return <div className="account-panel-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><aside ref={node => { dialogRef.current = node; }} className="self-account-panel" role="dialog" aria-modal="true" aria-labelledby="self-account-title">
    <header><div><h2 id="self-account-title">账户与设备</h2></div><button type="button" onClick={onClose} aria-label="关闭"><DismissRegular /></button></header>
    <div className="self-account-identity"><i>{profile.displayName.slice(0, 1)}</i><span><strong>{profile.displayName}</strong><small>{profile.email}</small></span></div>
    <section><div className="panel-section-title"><h3>授权设备</h3><span>{overview ? `${overview.devices.length}/${overview.deviceLimit}` : '—'}</span></div>
      {error ? <p className="form-error">{error}</p> : !overview ? <p className="panel-loading">正在读取设备…</p> : <div className="self-device-list">{overview.devices.map((device) => <div key={device.id}><span className={device.id === overview.currentDeviceId ? 'current-device-dot' : 'device-dot'} /><span><strong>{device.label}{device.id === overview.currentDeviceId && <em>当前</em>}</strong><small>最近使用 {formatDateTime(device.lastSeenAt)}</small></span></div>)}</div>}
      {overview && <p className="device-policy-note">七天内可自行替换 {overview.replacementLimit} 次，已使用 {overview.replacementsUsed} 次。设备名额已满时，新设备登录会进入替换流程。</p>}
    </section>
    <section><div className="panel-section-title"><h3>数据额度</h3></div><div className="quota-lines"><span>方案<strong>{overview?.usage.studies ?? profile.usage.studies} / {profile.studyLimit}</strong></span><span>求解记录<strong>{overview?.usage.results ?? profile.usage.results} / {profile.resultLimit}</strong></span></div></section>
    <section className="account-security-section"><div className="panel-section-title"><h3>登录安全</h3><button type="button" className="panel-text-action" onClick={() => { setPasswordOpen((open) => !open); setPasswordStatus(undefined); }} aria-expanded={passwordOpen}>{passwordOpen ? '收起' : '修改密码'}</button></div>
      {!passwordOpen && <p className="device-policy-note">使用当前密码验证身份后，可在这里更新登录密码。</p>}
      {passwordOpen && <PasswordChangeForm session={session} onCancel={() => { setPasswordOpen(false); setPasswordStatus(undefined); }} onSuccess={() => { setPasswordOpen(false); setPasswordStatus('密码已更新，下次登录请使用新密码。'); }} />}
      {passwordStatus && <p className="account-success" role="status">{passwordStatus}</p>}
    </section>
    <footer>{profile.role === 'admin' && <button type="button" className="admin-primary" onClick={onAdmin}>打开账户管理</button>}<button type="button" onClick={() => void signOut()}>退出登录</button></footer>
  </aside></div>;
}

function PasswordChangeForm({ session, onCancel, onSuccess }: { session: Session; onCancel: () => void; onSuccess: () => void }): JSX.Element {
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const valid = currentPassword.length >= 6 && nextPassword.length >= 8 && nextPassword === confirmation && nextPassword !== currentPassword;
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); if (!valid || pending) return;
    setPending(true); setError(undefined);
    try { await changePassword(session, currentPassword, nextPassword); onSuccess(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : '密码修改失败'); setPending(false); }
  };
  return <form className="password-change-form" onSubmit={(event) => void submit(event)}>
    <label>当前密码<PasswordInput autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoFocus required /></label>
    <div className="password-pair"><label>新密码<PasswordInput autoComplete="new-password" value={nextPassword} onChange={(event) => setNextPassword(event.target.value)} minLength={8} placeholder="至少 8 位" required /></label><label>确认新密码<PasswordInput autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={8} required /></label></div>
    {confirmation && nextPassword !== confirmation && <p className="field-hint error">两次输入的新密码不一致。</p>}
    {nextPassword && currentPassword === nextPassword && <p className="field-hint error">新密码不能与当前密码相同。</p>}
    {error && <p className="form-error" role="alert">{error}</p>}
    <div className="password-form-actions"><button type="button" onClick={onCancel}>取消</button><button type="submit" className="admin-primary" disabled={!valid || pending}>{pending ? '正在更新…' : '更新密码'}</button></div>
  </form>;
}

function CenteredState({ tone, title, detail, actionLabel, onAction }: { tone: 'loading' | 'error'; title: string; detail: string; actionLabel?: string; onAction?: () => void }): JSX.Element {
  return <main className={`web-centered ${tone}`} role={tone === 'error' ? 'alert' : 'status'}><span className="web-state-symbol">{tone === 'loading' ? <SpinnerIosRegular /> : <DismissRegular />}</span><h1>{title}</h1><p>{detail}</p>{actionLabel && <button type="button" onClick={onAction}>{actionLabel}</button>}</main>;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '—';
}
